import { formatMoneyByDecimal } from "@sembark-travel/money"
import { IAddCabMetricPrices, ICabPriceSeasons } from "./NewItem"
import { TCabMetricSheetFileData } from "./store"

export function transformMetricSheetFileDataToFormValues(
	cabMetricPriceSheetFileVersionData: TCabMetricSheetFileData
): IAddCabMetricPrices {
	const { seasons, prices, currency } = cabMetricPriceSheetFileVersionData

	const formattedSeasons: ICabPriceSeasons[] = (seasons || []).map(
		(season, index) => ({
			id: `season-${String(season.id || index)}`,
			name: season.name || `Season ${index + 1}`,
			date_ranges: (season.intervals || []).map((interval) => ({
				start_date: interval.start_date ? new Date(interval.start_date) : null,
				end_date: interval.end_date ? new Date(interval.end_date) : null,
			})),
			is_editing: false,
		})
	)

	const formattedCabPrices = prices.map((price) => ({
		id: String(price.id),
		cab_type: price.cab_type,
		cab_price_calculation_metrics: (price.metrics || []).map((metric) => {
			const seasonPrices = metric.prices
				? Object.entries(metric.prices).reduce((acc, [seasonId, priceInfo]) => {
						const customSeasonId = `season-${seasonId}`
						return {
							...acc,
							[customSeasonId]: {
								price: priceInfo.amount
									? Number(formatMoneyByDecimal(priceInfo.amount))
									: undefined,
							},
						}
					}, {})
				: {}

			return {
				metric: metric.metric,
				season_prices: seasonPrices,
			}
		}),
	}))

	return {
		config: {
			currency: currency,
		},
		seasons: formattedSeasons,
		cab_prices: formattedCabPrices,
	}
}

export function isSameCabPrice(
	originalItem: IAddCabMetricPrices["cab_prices"][number],
	modifiedItem: IAddCabMetricPrices["cab_prices"][number]
): boolean {
	if (!originalItem || !modifiedItem) return false

	const extractRelevantData = (
		item: IAddCabMetricPrices["cab_prices"][number]
	) => ({
		cabType: item.cab_type?.name,
		metrics: item.cab_price_calculation_metrics.map((metric) => ({
			metricName: metric.metric?.name,
			seasonPrices: Object.values(metric.season_prices)
				.map((sp) => sp.price)
				.sort(),
		})),
	})

	const originalData = extractRelevantData(originalItem)
	const modifiedData = extractRelevantData(modifiedItem)

	return JSON.stringify(originalData) === JSON.stringify(modifiedData)
}

export function findDiffBetweenCabMetricPrices(
	original: IAddCabMetricPrices["cab_prices"],
	modified: IAddCabMetricPrices["cab_prices"]
) {
	if (!original || !modified) {
		return null
	}

	function generateComparisonKey(
		row: IAddCabMetricPrices["cab_prices"][number]
	): string | null {
		if (!row?.cab_type) return null
		return `${row.cab_type.name}-${row.cab_price_calculation_metrics
			.map((metric) => metric?.metric?.name)
			.join("-")}`
	}

	const modifiedMap = new Map(
		(modified || []).map((price) => [generateComparisonKey(price), price])
	)

	let originalResult = [...original] as (Partial<
		IAddCabMetricPrices["cab_prices"][number]
	> & { __comparison: string; row_space?: number })[]

	let modifiedResult = [...modified] as (Partial<
		IAddCabMetricPrices["cab_prices"][number]
	> & {
		__comparison: string
		row_space?: number
	})[]

	let originalIndex = 0

	while (originalIndex < originalResult.length) {
		const originalItem = originalResult[originalIndex]
		const originalKey = generateComparisonKey(
			originalItem as IAddCabMetricPrices["cab_prices"][number]
		)
		const modifiedItem = modifiedMap.get(originalKey)

		if (modifiedItem) {
			if (
				!isSameCabPrice(
					originalItem as IAddCabMetricPrices["cab_prices"][number],
					modifiedItem
				)
			) {
				originalResult[originalIndex] = {
					...originalItem,
					__comparison: "changed",
				}

				const modifiedIndex = modifiedResult.findIndex(
					(item) =>
						generateComparisonKey(
							item as IAddCabMetricPrices["cab_prices"][number]
						) === generateComparisonKey(modifiedItem)
				)

				if (modifiedIndex !== -1) {
					modifiedResult[modifiedIndex] = {
						...modifiedItem,
						__comparison: "changed",
					}
				}
			} else {
				originalResult[originalIndex] = {
					...originalItem,
					__comparison: "same",
				}

				const modifiedIndex = modifiedResult.findIndex(
					(item) =>
						generateComparisonKey(
							item as IAddCabMetricPrices["cab_prices"][number]
						) === generateComparisonKey(modifiedItem)
				)

				if (modifiedIndex !== -1) {
					modifiedResult[modifiedIndex] = {
						...modifiedItem,
						__comparison: "same",
					}
				}
			}

			modifiedMap.delete(originalKey)
		} else {
			originalResult[originalIndex] = {
				...originalItem,
				__comparison: "removed",
			}
			modifiedResult.splice(originalIndex, 0, {
				row_space:
					(originalItem.cab_price_calculation_metrics || []).length || 1,
				__comparison: "removed",
			})
		}
		originalIndex++
	}

	for (const [, modifiedItem] of modifiedMap) {
		originalResult.push({
			__comparison: "added",
			row_space: (modifiedItem.cab_price_calculation_metrics || []).length || 1,
		})

		modifiedResult.push({ ...modifiedItem, __comparison: "added" })
	}

	const moveChangedToEnd = (
		arr: (Partial<IAddCabMetricPrices["cab_prices"][number]> & {
			__comparison: string
			row_space?: number
		})[]
	) => {
		const removedItems = arr.filter((item) => item.__comparison === "removed")
		const addedItems = arr.filter((item) => item.__comparison === "added")
		const changedItems = arr.filter((item) => item.__comparison === "changed")
		const sameItems = arr.filter((item) => item.__comparison === "same")
		return [...removedItems, ...addedItems, ...sameItems, ...changedItems]
	}

	originalResult = moveChangedToEnd(originalResult)
	modifiedResult = moveChangedToEnd(modifiedResult)

	return { original: originalResult, modified: modifiedResult }
}
